import { Stack, Text, Label } from '@fluentui/react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import { ConversationDetail, ConversationMessage, Citation, DocumentSnapshot } from '../../api/models'
import ChartFromJson, { isChartJson } from '../charts/ChartFromJson'
import { formatDateTime } from '../../utils/formatDateTime'

type ConversationDetailViewProps = {
  conversation: ConversationDetail
}

const ConversationDetailView = ({ conversation }: ConversationDetailViewProps) => (
  <>
    <Stack horizontal tokens={{ childrenGap: 24 }}>
      <Stack tokens={{ childrenGap: 4 }}>
        <Label>User</Label>
        <Text>{conversation.user_name} ({conversation.user_email})</Text>
      </Stack>
      <Stack tokens={{ childrenGap: 4 }}>
        <Label>Date</Label>
        <Text>{formatDateTime(conversation.created_at)}</Text>
      </Stack>
      <Stack tokens={{ childrenGap: 4 }}>
        <Label>Messages</Label>
        <Text styles={{ root: { fontWeight: 600 } }}>{conversation.message_count}</Text>
      </Stack>
      {conversation.plan_name && (
        <Stack tokens={{ childrenGap: 4 }}>
          <Label>Plan</Label>
          <Text>{conversation.plan_name}</Text>
        </Stack>
      )}
    </Stack>

    {conversation.documents && conversation.documents.length > 0 && (
      <Stack tokens={{ childrenGap: 6 }}>
        <Label>Documents Queried</Label>
        <Stack tokens={{ childrenGap: 4 }}>
          {conversation.documents.map((doc: DocumentSnapshot, idx: number) => (
            <Stack
              key={idx}
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: 8 }}
              styles={{
                root: {
                  padding: '6px 10px',
                  backgroundColor: '#f3f2f1',
                  borderRadius: 4,
                  borderLeft: '3px solid #2B3A67',
                },
              }}
            >
              <Text variant="small" styles={{ root: { color: '#323130' } }}>
                {doc.title || 'Untitled'}
                {doc.data_year ? ` (${doc.data_year})` : ''}
              </Text>
              {doc.url && (
                <a
                  href={doc.url.startsWith('http') ? doc.url : `https://query.cembenchmarking.com${doc.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 11, color: '#2B3A67' }}
                >
                  Open
                </a>
              )}
            </Stack>
          ))}
        </Stack>
      </Stack>
    )}

    <Stack
      tokens={{ childrenGap: 12 }}
      styles={{
        root: {
          maxHeight: '55vh',
          overflowY: 'auto',
          padding: '8px 0',
        },
      }}
    >
      {conversation.messages.map((msg: ConversationMessage, idx: number) => (
        <Stack
          key={msg.id || idx}
          styles={{
            root: {
              padding: '12px 16px',
              borderRadius: 8,
              backgroundColor: msg.role === 'user' ? '#E8EDF4' : '#f3f2f1',
              borderLeft: msg.role === 'user' ? '3px solid #2B3A67' : '3px solid #8a8886',
            },
          }}
          tokens={{ childrenGap: 6 }}
        >
          <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
            <Text
              variant="smallPlus"
              styles={{
                root: {
                  fontWeight: 600,
                  color: msg.role === 'user' ? '#2B3A67' : '#605e5c',
                  textTransform: 'capitalize',
                },
              }}
            >
              {msg.role}
            </Text>
            <Text variant="tiny" styles={{ root: { color: '#a19f9d' } }}>
              {formatDateTime(msg.created_at)}
            </Text>
          </Stack>
          {isChartJson(msg.content) ? (
            <ChartFromJson chartData={JSON.parse(msg.content.trim())} />
          ) : (
            <div className="markdown-content" style={{ fontSize: 13, lineHeight: '1.6', color: '#323130' }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                {msg.content}
              </ReactMarkdown>
            </div>
          )}
          {msg.feedback && (
            <Text
              variant="small"
              styles={{
                root: {
                  color: msg.feedback === 'positive' ? '#5B8A72' : '#D4775B',
                  fontWeight: 600,
                },
              }}
            >
              Feedback: {msg.feedback}
              {msg.comment ? ` - "${msg.comment}"` : ''}
            </Text>
          )}
          {msg.citations && msg.citations.length > 0 && (
            <Stack tokens={{ childrenGap: 4 }} styles={{ root: { marginTop: 6 } }}>
              <Text variant="small" styles={{ root: { fontWeight: 600, color: '#605e5c' } }}>
                Sources ({msg.citations.length}):
              </Text>
              {msg.citations.map((citation: Citation, cIdx: number) => (
                <Stack
                  key={cIdx}
                  horizontal
                  verticalAlign="center"
                  tokens={{ childrenGap: 6 }}
                  styles={{
                    root: {
                      padding: '4px 8px',
                      backgroundColor: '#edebe9',
                      borderRadius: 4,
                      fontSize: 12,
                    },
                  }}
                >
                  <Text variant="small" styles={{ root: { color: '#323130' } }}>
                    [{cIdx + 1}] {citation.title || citation.filepath || 'Untitled'}{citation.page_number != null ? ` (p. ${citation.page_number})` : ''}
                  </Text>
                  {citation.url && (
                    <a
                      href={citation.url.startsWith('http') ? citation.url : `https://query.cembenchmarking.com${citation.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 11, color: '#2B3A67' }}
                    >
                      Open
                    </a>
                  )}
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      ))}
    </Stack>
  </>
)

export default ConversationDetailView
